import { useState, useEffect } from "react";

const STORAGE_KEY = "a11y_settings";

const defaultSettings = {
  textSize: 0,
  grayscale: false,
  highContrast: false,
  negativeContrast: false,
  lightBg: false,
  linksUnderline: false,
  readableFont: false,
};

function loadSettings() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  } catch {
    return defaultSettings;
  }
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);

  const isActive = settings.textSize !== 0 || settings.grayscale || settings.highContrast || settings.negativeContrast || settings.lightBg || settings.linksUnderline || settings.readableFont;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    const el = document.getElementById("main-wrapper");
    if (!el) return;

    // Text size — use CSS zoom for reliable scaling across all elements
    if (settings.textSize > 0) {
      for (let i = 0; i < settings.textSize; i++) {
        el.querySelectorAll("p, span, li, h1, h2, h3, h4, a, td, th, label, summary, button").forEach((node) => {
          const current = parseFloat(window.getComputedStyle(node).fontSize);
          node.style.fontSize = `${current * 1.12}px`;
        });
      }
    } else if (settings.textSize < 0) {
      for (let i = 0; i > settings.textSize; i--) {
        el.querySelectorAll("p, span, li, h1, h2, h3, h4, a, td, th, label, summary, button").forEach((node) => {
          const current = parseFloat(window.getComputedStyle(node).fontSize);
          node.style.fontSize = `${current * 0.88}px`;
        });
      }
    } else {
      el.querySelectorAll("p, span, li, h1, h2, h3, h4, a, td, th, label, summary, button").forEach((node) => {
        node.style.fontSize = "";
      });
    }

    // Filters
    const filters = [];
    if (settings.grayscale) filters.push("grayscale(100%)");
    if (settings.highContrast) filters.push("contrast(1.5)");
    if (settings.negativeContrast) filters.push("invert(1)");
    el.style.filter = filters.length ? filters.join(" ") : "";

    // Light background
    el.style.backgroundColor = settings.lightBg ? "#ffffff" : "";

    // Links underline
    const links = el.querySelectorAll("a");
    links.forEach((a) => {
      a.style.textDecoration = settings.linksUnderline ? "underline" : "";
    });

    // Readable font
    el.style.fontFamily = settings.readableFont ? "Arial, Helvetica, sans-serif" : "";
    el.style.letterSpacing = settings.readableFont ? "0.05em" : "";
    el.style.wordSpacing = settings.readableFont ? "0.1em" : "";
  }, [settings]);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));
  const reset = () => setSettings(defaultSettings);

  const options = [
    { label: "Increase Text", icon: "A+", action: () => update("textSize", Math.min(settings.textSize + 1, 4)) },
    { label: "Decrease Text", icon: "A-", action: () => update("textSize", Math.max(settings.textSize - 1, -2)) },
    { label: "Grayscale", icon: "G", action: () => update("grayscale", !settings.grayscale), active: settings.grayscale },
    { label: "High Contrast", icon: "HC", action: () => update("highContrast", !settings.highContrast), active: settings.highContrast },
    { label: "Negative Contrast", icon: "NC", action: () => update("negativeContrast", !settings.negativeContrast), active: settings.negativeContrast },
    { label: "Light Background", icon: "LB", action: () => update("lightBg", !settings.lightBg), active: settings.lightBg },
    { label: "Links Underline", icon: "U", action: () => update("linksUnderline", !settings.linksUnderline), active: settings.linksUnderline },
    { label: "Readable Font", icon: "RF", action: () => update("readableFont", !settings.readableFont), active: settings.readableFont },
  ];

  return (
    <>
      {/* Toggle button — wheelchair icon on left side */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open accessibility settings"
        data-testid="accessibility-toggle"
        className="fixed left-4 z-[60] w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-colors"
        style={{ top: 80, backgroundColor: isActive ? "#F59E0B" : "#1f2937", color: "#fff" }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="4" r="2"/>
          <path d="M19 13v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H13c-.35-.2-.75-.3-1.19-.26C10.76 7.11 10 8.04 10 9.09V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-6.17 5c-.41 1.16-1.52 2-2.83 2-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07z"/>
        </svg>
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="accessibility-panel"
          role="dialog"
          aria-label="Accessibility settings"
          className="fixed left-4 z-[60] bg-white rounded-xl shadow-2xl border border-gray-200 w-[240px] p-4 animate-fade-in"
          style={{ top: 100 }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-gray-900">Accessibility</p>
            <button onClick={() => setOpen(false)} aria-label="Close accessibility panel" className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
          </div>

          <div className="space-y-1.5">
            {options.map((opt) => (
              <button
                key={opt.label}
                onClick={opt.action}
                aria-label={opt.label}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-xs font-medium transition-colors ${
                  opt.active ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-transparent"
                }`}
              >
                <span className="w-7 h-7 rounded-md bg-gray-200 flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={opt.active ? { backgroundColor: "#F59E0B", color: "#fff" } : {}}>
                  {opt.icon}
                </span>
                {opt.label}
              </button>
            ))}
          </div>

          {isActive && (
            <button
              onClick={reset}
              aria-label="Reset all accessibility settings"
              data-testid="accessibility-reset"
              className="w-full mt-3 py-2 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              Reset All
            </button>
          )}
        </div>
      )}
    </>
  );
}
